import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus, Tag, Gift, TrendingUp } from 'lucide-react';
import { useGalleryCart } from '../context/GalleryCartContext';
import { useGalleryTheme } from '../context/GalleryThemeContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { PricingService } from '../../../services/pricingService';
import Button from '../components/common/Button';

const GalleryCartPage: React.FC = () => {
  const navigate = useNavigate();
  const { cart, removeItem, updateQuantity, removePackage, clearCart } = useGalleryCart();
  const { theme } = useGalleryTheme();
  const { isAuthenticated } = useCustomerAuth();

  // Calculate dynamic pricing for individual items
  const itemsPricing = useMemo(() => {
    const totalIndividualPhotos = cart.items.reduce((sum, item) => sum + item.quantity, 0);
    if (totalIndividualPhotos === 0) return null;
    return PricingService.calculatePrice(totalIndividualPhotos);
  }, [cart.items]);

  // Get next tier info for upselling
  const nextTierInfo = useMemo(() => {
    const totalIndividualPhotos = cart.items.reduce((sum, item) => sum + item.quantity, 0);
    return PricingService.getNextTierInfo(totalIndividualPhotos);
  }, [cart.items]);

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/checkout' } });
    } else {
      navigate('/checkout');
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: cart.currency,
    }).format(price);
  };

  if (cart.items.length === 0 && cart.packages.length === 0) {
    return (
      <div className="text-center py-12">
        <ShoppingCart
          size={64}
          className="mx-auto mb-4"
          style={{ color: theme.colors.text.muted }}
        />
        <h2 className="text-2xl font-bold mb-4" style={{ color: theme.colors.text.primary }}>
          Your cart is empty
        </h2>
        <p className="text-lg mb-6" style={{ color: theme.colors.text.secondary }}>
          Add some photos to your cart to get started
        </p>
        <Link to="/events">
          <Button>Browse Galleries</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2" style={{ color: theme.colors.text.primary }}>
          Shopping Cart
        </h1>
        <p style={{ color: theme.colors.text.secondary }}>
          {cart.items.length + cart.packages.length} item
          {cart.items.length + cart.packages.length !== 1 ? 's' : ''} in your cart
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4">
          {/* Individual Photos */}
          {cart.items.map(item => (
            <div
              key={item.id}
              className="flex items-center space-x-4 p-4 rounded-lg border"
              style={{
                backgroundColor: theme.colors.surface,
                borderColor: theme.colors.border,
              }}
            >
              {/* Photo Thumbnail */}
              <img
                src={item.photo.urls.thumbnail}
                alt={item.photo.title || 'Photo'}
                className="w-20 h-20 object-cover rounded"
              />

              {/* Item Details */}
              <div className="flex-1">
                <h4 className="font-medium" style={{ color: theme.colors.text.primary }}>
                  {item.photo.title || `Photo ${item.photoId}`}
                </h4>
                <p className="text-sm" style={{ color: theme.colors.text.secondary }}>
                  Resolution: {item.resolution}
                </p>
                <p className="text-sm" style={{ color: theme.colors.text.secondary }}>
                  {formatPrice(item.price)} each
                </p>
              </div>

              {/* Quantity Controls */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => updateQuantity(item.id, item.quantity - 1)}
                  className="p-1 rounded hover:bg-gray-100"
                  disabled={item.quantity <= 1}
                >
                  <Minus size={16} />
                </button>
                <span className="w-8 text-center">{item.quantity}</span>
                <button
                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                  className="p-1 rounded hover:bg-gray-100"
                >
                  <Plus size={16} />
                </button>
              </div>

              {/* Item Total */}
              <div className="text-right">
                <p className="font-medium" style={{ color: theme.colors.text.primary }}>
                  {formatPrice(item.price * item.quantity)}
                </p>
              </div>

              {/* Remove Button */}
              <button
                onClick={() => removeItem(item.id)}
                className="p-2 text-red-500 hover:bg-red-50 rounded"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}

          {/* Package Deals */}
          {cart.packages.map(pkg => (
            <div
              key={pkg.id}
              className="flex items-center space-x-4 p-4 rounded-lg border"
              style={{
                backgroundColor: theme.colors.surface,
                borderColor: theme.colors.border,
              }}
            >
              <div className="flex-1">
                <h4 className="font-medium" style={{ color: theme.colors.text.primary }}>
                  {pkg.packageName}
                </h4>
                <p className="text-sm" style={{ color: theme.colors.text.secondary }}>
                  {pkg.photoCount} photos selected
                </p>
                <p className="text-sm text-green-600">
                  Save {formatPrice(pkg.originalPrice - pkg.price)}
                </p>
              </div>

              <div className="text-right">
                <p className="text-sm line-through" style={{ color: theme.colors.text.muted }}>
                  {formatPrice(pkg.originalPrice)}
                </p>
                <p className="font-medium" style={{ color: theme.colors.text.primary }}>
                  {formatPrice(pkg.price)}
                </p>
              </div>

              <button
                onClick={() => removePackage(pkg.id)}
                className="p-2 text-red-500 hover:bg-red-50 rounded"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div>
          <div
            className="p-6 rounded-lg border sticky top-4"
            style={{
              backgroundColor: theme.colors.surface,
              borderColor: theme.colors.border,
            }}
          >
            <h3 className="text-lg font-semibold mb-4" style={{ color: theme.colors.text.primary }}>
              Order Summary
            </h3>

            <div className="space-y-2 mb-4">
              <div className="flex justify-between">
                <span style={{ color: theme.colors.text.secondary }}>Subtotal</span>
                <span style={{ color: theme.colors.text.primary }}>
                  {formatPrice(cart.subtotal)}
                </span>
              </div>

              {/* Dynamic Volume Discount */}
              {itemsPricing && itemsPricing.volumeDiscount > 0 && (
                <div className="flex justify-between text-green-600">
                  <div className="flex items-center gap-1">
                    <TrendingUp size={14} />
                    <span className="text-sm">Volume Discount</span>
                  </div>
                  <span>-{formatPrice(itemsPricing.volumeDiscount)}</span>
                </div>
              )}

              {/* Package Savings */}
              {cart.packages.length > 0 && (
                <div className="flex justify-between text-green-600">
                  <div className="flex items-center gap-1">
                    <Gift size={14} />
                    <span className="text-sm">Bundle Savings</span>
                  </div>
                  <span>
                    -{formatPrice(cart.packages.reduce((sum, pkg) => sum + pkg.discount, 0))}
                  </span>
                </div>
              )}

              {/* Promo Code Discount */}
              {cart.discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <div className="flex items-center gap-1">
                    <Tag size={14} />
                    <span className="text-sm">Promo Code</span>
                  </div>
                  <span>-{formatPrice(cart.discount)}</span>
                </div>
              )}

              <div className="flex justify-between">
                <span style={{ color: theme.colors.text.secondary }}>Tax</span>
                <span style={{ color: theme.colors.text.primary }}>{formatPrice(cart.tax)}</span>
              </div>
            </div>

            {/* Total Savings Display */}
            {(itemsPricing?.savings ||
              cart.packages.reduce((sum, pkg) => sum + pkg.discount, 0) ||
              cart.discount) > 0 && (
              <div
                className="mb-4 p-3 rounded-lg text-center"
                style={{
                  backgroundColor: `${theme.colors.success}15`,
                  color: theme.colors.success,
                }}
              >
                <div className="flex items-center justify-center gap-1 font-semibold">
                  <Gift size={16} />
                  <span>
                    Total Savings:{' '}
                    {formatPrice(
                      (itemsPricing?.savings || 0) +
                        cart.packages.reduce((sum, pkg) => sum + pkg.discount, 0) +
                        cart.discount
                    )}
                  </span>
                </div>
              </div>
            )}

            <div className="border-t pt-4 mb-6" style={{ borderTopColor: theme.colors.border }}>
              <div className="flex justify-between">
                <span
                  className="text-lg font-semibold"
                  style={{ color: theme.colors.text.primary }}
                >
                  Total
                </span>
                <span
                  className="text-lg font-semibold"
                  style={{ color: theme.colors.text.primary }}
                >
                  {formatPrice(cart.total)}
                </span>
              </div>
            </div>

            {/* Next Tier Upsell */}
            {nextTierInfo && (
              <div
                className="mb-4 p-3 rounded-lg border-2 border-dashed text-center"
                style={{
                  borderColor: theme.colors.primary,
                  backgroundColor: `${theme.colors.primary}10`,
                }}
              >
                <div
                  className="text-sm font-medium mb-1"
                  style={{ color: theme.colors.text.primary }}
                >
                  🎯 Add {nextTierInfo.photosNeeded} more photos
                </div>
                <div className="text-xs" style={{ color: theme.colors.text.secondary }}>
                  Get {Math.round(nextTierInfo.discount * 100)}% off your entire order!
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Button onClick={handleCheckout} fullWidth size="lg">
                Proceed to Checkout
              </Button>

              <Button onClick={clearCart} variant="ghost" fullWidth>
                Clear Cart
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GalleryCartPage;
